/**
 * Onboarding Client Care Pack — assembly + send dialog.
 *
 * Opens from Authorities → Onboarding → "Send Client Care Pack".
 *
 * Two things happen from one dialog:
 *
 *   1. Generate DOCX-based pack documents (CCL, T&Cs, custom letters) —
 *      mail-merged from the firm's bound Doc Templates and filed into a
 *      date-stamped "Onboarding Pack — DD-MMM-YYYY" folder on the case.
 *
 *   2. Email each client on the case a magic-link that drops them straight
 *      into the 5-step onboarding wizard (Payment · ID/AML · CCL sign ·
 *      Questionnaires · TA forms). The wizard handles the interactive
 *      slots (questionnaires + TA fillable PDFs) natively — so the pack
 *      dialog shows those slots as informational rows, not bindable.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import API, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { CheckCircle, FileText, Package, Warning, ArrowSquareOut, Sparkle, EnvelopeSimple, Eye, PencilSimple, FloppyDisk, ArrowCounterClockwise } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import CclBody from "@/components/app/ccl/CclBody";
import CclEditor from "@/components/app/ccl/CclEditor";

// Wizard questionnaire pack-slots ↔ the `case_questionnaires` key they
// assign. TA slots (ta6/ta7/ta10) are NOT here — they're driven by the
// TA step, not questionnaire rows, so they stay informational.
const QSLOT_TO_KEY = {
  source_of_funds: "source_of_funds",
  personal_info: "personal_info",
  purchase_questionnaire: "purchase",
  sale_questionnaire: "sale",
  remortgage_questionnaire: "remortgage",
  transfer_questionnaire: "transfer",
};

function CclPreviewDialog({ open, onOpenChange, caseId, fileHandlerId, onOverrideChange }) {
  const [mode, setMode] = useState("preview");        // preview | edit
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");               // rendered preview text
  const [error, setError] = useState("");
  const [draftPreview, setDraftPreview] = useState(false);
  const [feesLinked, setFeesLinked] = useState(true); // false → fees hand-typed, not auto-pulling
  const [relinking, setRelinking] = useState(false);

  // edit state (per-case override template)
  const [editBody, setEditBody] = useState("");
  const [savedBody, setSavedBody] = useState("");
  const [hasOverride, setHasOverride] = useState(false);
  const [mergeTags, setMergeTags] = useState([]);
  const [editLoading, setEditLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadRendered = useCallback(async () => {
    setLoading(true); setError(""); setDraftPreview(false);
    const qs = fileHandlerId ? `?file_handler_id=${encodeURIComponent(fileHandlerId)}` : "";
    try {
      const r = await API.get(`/onboarding/cases/${caseId}/ccl${qs}`);
      const forms = r.data?.forms || [];
      const ccl = forms.find((f) => f.form === "ccl") || forms[0];
      setBody(ccl?.body || r.data?.body || "");
      setFeesLinked(ccl?.fees_linked !== false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not load the Client Care Letter draft");
    } finally { setLoading(false); }
  }, [caseId, fileHandlerId]);

  const loadOverride = useCallback(async () => {
    setEditLoading(true);
    try {
      const r = await API.get(`/onboarding/cases/${caseId}/ccl-override`);
      setEditBody(r.data.body || "");
      setSavedBody(r.data.has_override ? (r.data.body || "") : "");
      setHasOverride(!!r.data.has_override);
      setMergeTags(r.data.merge_tags || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load the letter for editing");
    } finally { setEditLoading(false); }
  }, [caseId]);

  useEffect(() => {
    if (!open) return;
    setMode("preview");
    loadRendered();
    loadOverride();
  }, [open, loadRendered, loadOverride]);

  const startEdit = async () => { if (!editBody && !savedBody) await loadOverride(); setMode("edit"); };

  const previewDraft = async () => {
    setLoading(true); setError("");
    try {
      const r = await API.post(`/onboarding/cases/${caseId}/ccl/preview`,
        { body: editBody, file_handler_id: fileHandlerId || null });
      setBody(r.data.body || ""); setDraftPreview(true); setMode("preview");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Preview failed");
    } finally { setLoading(false); }
  };

  const saveOverride = async () => {
    setSaving(true);
    try {
      const r = await API.put(`/onboarding/cases/${caseId}/ccl-override`, { body: editBody });
      setHasOverride(!!r.data.has_override);
      setSavedBody(r.data.has_override ? editBody : "");
      onOverrideChange?.(!!r.data.has_override);
      toast.success("Saved — this is the letter your client will be sent for this case.");
      await loadRendered(); setMode("preview");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const revertOverride = async () => {
    setSaving(true);
    try {
      await API.put(`/onboarding/cases/${caseId}/ccl-override`, { body: "" });
      await loadOverride();
      onOverrideChange?.(false);
      toast.success("Reverted to the firm master letter.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Revert failed");
    } finally { setSaving(false); }
  };

  const dirty = editBody !== savedBody && editBody.trim() !== "";

  const relinkFees = async () => {
    setRelinking(true);
    try {
      await API.post(`/onboarding/cases/${caseId}/ccl/relink-fees`);
      await loadOverride();
      onOverrideChange?.(false);
      await loadRendered();
      toast.success("Fees re-linked — the letter now pulls fees from this case's completion statement.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not re-link fees");
    } finally { setRelinking(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="ccl-preview-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }} className="flex items-center gap-2 flex-wrap">
            {mode === "edit" ? "Client Care Letter — edit for this case" : "Client Care Letter — draft preview"}
            {hasOverride && (
              <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold">
                Custom for this case
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {mode === "edit"
              ? "Edit the wording below, then Preview or Save. Your changes apply to THIS client only — the firm master template is untouched. Merge tags are filled in automatically."
              : "This is the letter your client will be asked to read and sign in the wizard. The fees below are pulled automatically from the rows ticked on this case's completion statement."}
          </DialogDescription>
        </DialogHeader>

        {mode === "edit" ? (
          <div className="flex-1 overflow-y-auto" data-testid="ccl-edit-region">
            <CclEditor value={editBody} onChange={setEditBody} disabled={editLoading || saving}
              testid="ccl-edit-textarea-pack" heightClass="h-[48vh]" />
            {mergeTags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="ccl-merge-tags-pack">
                <span className="text-[11px] text-slate-500 mr-1">Merge tags:</span>
                {mergeTags.map((t) => (
                  <code key={t} className="text-[10px] bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-slate-700">{t}</code>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto rounded-md border border-slate-200 bg-slate-50/60 p-4"
            data-testid="ccl-preview-body">
            {!loading && !error && !feesLinked && (
              <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5" data-testid="ccl-fees-unlinked-warning">
                <p className="text-[12px] font-semibold text-amber-800">Fees aren't linked to the completion statement</p>
                <p className="text-[11px] text-amber-700 mt-0.5">
                  This letter's fees were typed in by hand, so they won't update from the fee rows ticked on this case.
                  Restore automatic fees to pull them from the completion statement.
                </p>
                <Button size="sm" onClick={relinkFees} disabled={relinking}
                  className="mt-2 h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white" data-testid="ccl-relink-fees-btn">
                  <ArrowCounterClockwise weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                  {relinking ? "Restoring…" : "Restore automatic fees"}
                </Button>
              </div>
            )}
            {draftPreview && (
              <div className="mb-2 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                Showing your unsaved draft — Save to send this version.
              </div>
            )}
            {loading ? (
              <div className="text-sm text-slate-500 py-8 text-center">Loading draft…</div>
            ) : error ? (
              <div className="text-sm text-rose-600 py-8 text-center">{error}</div>
            ) : (
              <CclBody text={body} className="font-sans text-[13px] leading-relaxed text-slate-800" />
            )}
          </div>
        )}

        <DialogFooter className="border-t border-slate-100 pt-3 flex-col-reverse sm:flex-row gap-2">
          {mode === "edit" ? (
            <>
              <Button variant="outline" onClick={() => setMode("preview")} disabled={saving} data-testid="ccl-edit-cancel">
                Back to preview
              </Button>
              <div className="flex items-center gap-2 sm:ml-auto flex-wrap">
                <Button variant="outline" onClick={revertOverride} disabled={saving || !hasOverride}
                  className="text-slate-600" data-testid="ccl-edit-revert">
                  <ArrowCounterClockwise weight="bold" className="w-3.5 h-3.5 mr-1.5" /> Revert to master
                </Button>
                <Button variant="outline" onClick={previewDraft} disabled={saving || editLoading}
                  className="border-[#100f5d]/30 text-[#100f5d] hover:bg-[#100f5d]/5" data-testid="ccl-edit-preview">
                  <Eye weight="bold" className="w-3.5 h-3.5 mr-1.5" /> Preview
                </Button>
                <Button onClick={saveOverride} disabled={saving || editLoading || !dirty}
                  className="bg-[#100f5d] hover:bg-[#0b0a3d] text-white" data-testid="ccl-edit-save">
                  <FloppyDisk weight="bold" className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save for this case"}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ccl-preview-close">Close</Button>
              <Button onClick={startEdit} disabled={loading || editLoading}
                className="sm:ml-auto bg-[#100f5d] hover:bg-[#0b0a3d] text-white" data-testid="ccl-preview-edit-btn">
                <PencilSimple weight="bold" className="w-3.5 h-3.5 mr-1.5" /> {editLoading ? "Opening…" : "Edit this letter"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocxSlotRow({ slot, checked, onToggle, disabled }) {
  const tplName = slot.template?.name;
  return (
    <label
      className={`flex items-start gap-3 p-3 rounded-md border transition ${
        disabled ? "border-slate-200 bg-slate-50/60 opacity-70 cursor-not-allowed"
                 : checked ? "border-[#1462a0]/40 bg-[#1462a0]/5"
                 : "border-slate-200 bg-white hover:bg-slate-50 cursor-pointer"
      }`}
      data-testid={`pack-slot-row-${slot.slot}`}
    >
      <input type="checkbox" checked={checked && !disabled} disabled={disabled}
        onChange={onToggle} className="mt-1 w-4 h-4"
        data-testid={`pack-slot-check-${slot.slot}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-900">{slot.label}</span>
          {!slot.applicable && (
            <span className="text-[10px] uppercase tracking-wide bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-semibold">Not applicable</span>
          )}
          {slot.applicable && !slot.template && (
            <span className="text-[10px] uppercase tracking-wide bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded font-semibold inline-flex items-center gap-1">
              <Warning weight="fill" className="w-3 h-3" /> No template bound
            </span>
          )}
          {slot.template && (
            <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-semibold inline-flex items-center gap-1">
              <CheckCircle weight="fill" className="w-3 h-3" /> Bound
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-0.5">{slot.description}</p>
        {tplName && (
          <p className="text-[11px] text-slate-600 mt-1">
            <FileText weight="bold" className="inline w-3 h-3 mr-1 -mt-0.5" />
            Template: <span className="font-medium text-slate-800">{tplName}</span>
          </p>
        )}
        {slot.applicable && !slot.template && (
          <Link to="/templates" className="text-[11px] text-[#1462a0] underline inline-flex items-center gap-1 mt-1 hover:opacity-80">
            <ArrowSquareOut weight="bold" className="w-3 h-3" /> Bind a template in Doc Templates
          </Link>
        )}
      </div>
    </label>
  );
}

function WizardSlotRow({ slot, selectable = false, checked = false, onToggle }) {
  // TA slots stay informational; questionnaire slots are selectable so the
  // solicitor can manually add/remove which forms the client is sent.
  const isTa = slot.slot.startsWith("ta");
  const active = selectable ? checked : slot.applicable;
  const row = (
    <div className={`flex items-start gap-3 p-3 rounded-md border ${selectable ? "border-solid cursor-pointer" : "border-dashed"} ${
      active ? "border-indigo-200 bg-indigo-50/40" : "border-slate-200 bg-slate-50/60 opacity-70"
    }`} data-testid={`pack-wizard-row-${slot.slot}`}>
      {selectable ? (
        <input type="checkbox" checked={checked} onChange={onToggle} onClick={(e) => e.stopPropagation()}
          className="mt-1 w-4 h-4 shrink-0" data-testid={`pack-wizard-check-${slot.slot}`} />
      ) : (
        <div className="mt-0.5">
          <Sparkle weight="fill" className={`w-4 h-4 ${slot.applicable ? "text-indigo-500" : "text-slate-300"}`} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-900">{slot.label}</span>
          {selectable ? (
            <>
              {!slot.applicable && (
                <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold">Manually added</span>
              )}
              {checked && slot.applicable && (
                <span className="text-[10px] uppercase tracking-wide bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-semibold">Will send</span>
              )}
              {!checked && (
                <span className="text-[10px] uppercase tracking-wide bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-semibold">Not sent</span>
              )}
            </>
          ) : (
            <>
              {!slot.applicable && (
                <span className="text-[10px] uppercase tracking-wide bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-semibold">Not applicable</span>
              )}
              {slot.applicable && (
                <span className="text-[10px] uppercase tracking-wide bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-semibold">In wizard</span>
              )}
            </>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-0.5">{slot.description}</p>
        {isTa && slot.applicable && (
          <Link to="/settings/ta-templates" className="text-[11px] text-[#1462a0] underline inline-flex items-center gap-1 mt-1 hover:opacity-80">
            <ArrowSquareOut weight="bold" className="w-3 h-3" /> Upload / update the master PDF
          </Link>
        )}
      </div>
    </div>
  );
  if (selectable) {
    return <label className="block">{row}</label>;
  }
  return row;
}

export default function OnboardingPackDialog({ open, onOpenChange, caseId, onGenerated }) {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState({ slots: [], clients: [] });
  const [picked, setPicked] = useState(new Set());
  const [qPicked, setQPicked] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [sendLink, setSendLink] = useState(true);   // default ON — one-click
  const [previewOpen, setPreviewOpen] = useState(false);
  const [cclOverride, setCclOverride] = useState(false);
  const [handlers, setHandlers] = useState([]);
  const [fileHandlerId, setFileHandlerId] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    // Fetch pack preview + case (for client emails to show recipients).
    Promise.all([
      API.get(`/cases/${caseId}/onboarding-pack/preview`).then(r => r.data),
      API.get(`/cases/${caseId}`).then(r => r.data).catch(() => ({})),
      API.get(`/onboarding/file-handlers`).then(r => r.data).catch(() => []),
    ]).then(([packData, caseData, handlerList]) => {
      if (cancelled) return;
      setPreview({ ...packData, clients: caseData.clients || [] });
      setHandlers(handlerList || []);
      // Default the file handler to whatever's already stored on the case.
      setFileHandlerId(caseData.file_handler_id || "");
      const def = new Set();
      (packData.slots || []).forEach((s) => {
        if (s.kind === "docx" && s.default_selected && s.template) def.add(s.slot);
      });
      setPicked(def);
      // Questionnaire forms: default-tick the ones applicable to this
      // matter — the solicitor can add/remove any of them manually.
      const qDef = new Set();
      (packData.slots || []).forEach((s) => {
        if (QSLOT_TO_KEY[s.slot] && s.applicable) qDef.add(s.slot);
      });
      setQPicked(qDef);
    }).catch((err) => {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load pack");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, caseId]);

  const toggle = (slot) => {
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(slot)) next.delete(slot);
      else next.add(slot);
      return next;
    });
  };

  const toggleQ = (slot) => {
    setQPicked((s) => {
      const next = new Set(s);
      if (next.has(slot)) next.delete(slot);
      else next.add(slot);
      return next;
    });
  };

  const handleSubmit = async () => {
    const slots = Array.from(picked);
    if (!slots.length && !sendLink) {
      return toast.error("Tick at least one document, or enable 'Send onboarding link'.");
    }
    if (sendLink && (preview.clients || []).filter(c => c.email).length === 0) {
      return toast.error("No client emails on this case — add a client with an email before sending.");
    }
    setSubmitting(true);
    const label = sendLink && slots.length ? "Assembling pack & sending onboarding link…"
                : sendLink                  ? "Sending onboarding link…"
                                            : `Assembling ${slots.length} document${slots.length === 1 ? "" : "s"}…`;
    const tid = toast.loading(label);
    try {
      const questionnaire_keys = Array.from(qPicked)
        .map((slot) => QSLOT_TO_KEY[slot])
        .filter(Boolean);
      const { data } = await API.post(`/cases/${caseId}/onboarding-pack/generate`,
        { slots, send_onboarding_link: sendLink, file_handler_id: fileHandlerId || null, questionnaire_keys });
      const bits = [];
      if (data.count) bits.push(`${data.count} document${data.count === 1 ? "" : "s"} filed under '${data.folder}'`);
      if (data.invites && data.invites.length) {
        const sent = data.invites.filter(i => i.ok).length;
        bits.push(`onboarding link emailed to ${sent}/${data.invites.length} client(s)`);
      }
      toast.success(bits.join(" · "), { id: tid });
      onGenerated?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Pack action failed", { id: tid });
    } finally {
      setSubmitting(false);
    }
  };

  const allSlots = preview.slots || [];
  const docxSlots = useMemo(() => allSlots.filter(s => (s.kind || "docx") === "docx"), [allSlots]);
  // Only show questionnaire / TA forms that actually apply to this matter
  // type — listing forms that can never be sent (e.g. a Purchase
  // questionnaire on a Sale) just causes confusion, so they're hidden.
  const wizardSlots = useMemo(() => allSlots.filter(s => s.kind === "wizard" && s.applicable), [allSlots]);
  const bound = useMemo(() => docxSlots.filter((s) => !!s.template).length, [docxSlots]);
  const recipients = useMemo(() =>
    (preview.clients || []).filter(c => c.email).map(c => c.email), [preview.clients]);

  const primaryLabel =
    picked.size && sendLink   ? `Generate pack (${picked.size}) & send onboarding link`
  : picked.size               ? `Generate pack (${picked.size})`
  : sendLink                  ? "Send onboarding link"
                              : "Nothing selected";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="pack-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>Send Client Care Pack</DialogTitle>
          <DialogDescription>
            Assemble the pack documents you send (CCL, T&amp;Cs, custom letters) AND fire off the one-click magic link that lands your client in the 5-step onboarding wizard.
            {preview.transaction_type && (
              <span className="block text-[11px] text-slate-500 mt-1">
                Tailored for <strong>{preview.transaction_type}</strong>{preview.tenure ? ` · ${preview.tenure}` : ""}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-sm text-slate-500 p-6 text-center">Loading pack slots…</div>
        ) : (
          <>
            {/* DOCX pack documents (mail-merged & filed) */}
            <div className="mt-2">
              <div className="flex items-center justify-between mb-2 px-1">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Pack documents</p>
                <p className="text-[11px] text-slate-500">{bound} template{bound === 1 ? "" : "s"} bound</p>
              </div>
              <div className="space-y-2" data-testid="pack-slot-list">
                {docxSlots.map((s) => (
                  <DocxSlotRow key={s.slot} slot={s} checked={picked.has(s.slot)}
                    onToggle={() => toggle(s.slot)}
                    disabled={!s.applicable || !s.template} />
                ))}
                {!docxSlots.length && <p className="text-xs text-slate-400 px-1">No DOCX slots configured.</p>}
              </div>
            </div>

            {/* Wizard-native slots — questionnaires are add/removable, TA info-only */}
            {wizardSlots.length > 0 && (
              <div className="mt-4">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2 px-1">
                  Forms sent to the client
                </p>
                <div className="space-y-2">
                  {wizardSlots.map((s) => (
                    <WizardSlotRow key={s.slot} slot={s}
                      selectable={!!QSLOT_TO_KEY[s.slot]}
                      checked={qPicked.has(s.slot)}
                      onToggle={() => toggleQ(s.slot)} />
                  ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-2 px-1">
                  Only the forms that apply to this matter type are shown — untick any you don't want to send. TA forms are completed by the client in the wizard.
                </p>
              </div>
            )}

            {/* Send onboarding link */}
            <div className="mt-5 p-3 rounded-md border border-[#100f5d]/20 bg-[#100f5d]/[0.03]"
              data-testid="pack-send-link-block">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={sendLink} onChange={(e) => setSendLink(e.target.checked)}
                  className="mt-1 w-4 h-4" data-testid="pack-send-link-check" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    <EnvelopeSimple weight="bold" className="w-4 h-4 text-[#100f5d]" />
                    Send onboarding link to clients
                  </div>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Emails each client a one-click magic link that drops them into the 5-step wizard
                    (Payment · ID/AML · CCL sign · Questionnaires · TA forms). No password required.
                  </p>
                  {recipients.length > 0 ? (
                    <p className="text-[11px] text-slate-500 mt-1.5">
                      Will email: <span className="font-mono">{recipients.join(", ")}</span>
                    </p>
                  ) : (
                    <p className="text-[11px] text-rose-600 mt-1.5">
                      No client emails on this case yet — add one before sending.
                    </p>
                  )}
                  <div className="mt-3">
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1" htmlFor="pack-file-handler">
                      File handler (named on the Client Care Letter)
                    </label>
                    <select
                      id="pack-file-handler"
                      value={fileHandlerId}
                      onChange={(e) => setFileHandlerId(e.target.value)}
                      className="w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#100f5d]/30"
                      data-testid="pack-file-handler-select">
                      <option value="">— To be allocated —</option>
                      {handlers.map((h) => (
                        <option key={h.id} value={h.id}>{h.name}</option>
                      ))}
                    </select>
                    <p className="text-[11px] text-slate-500 mt-1">
                      The matter sits with onboarding first — pick the fee earner who will actually handle the file.
                      Supervision is shown as Michael Tucker.
                    </p>
                  </div>
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={(e) => { e.preventDefault(); setPreviewOpen(true); }}
                    className="mt-2.5 h-8 text-xs border-[#100f5d]/30 text-[#100f5d] hover:bg-[#100f5d]/5"
                    data-testid="pack-preview-ccl-btn">
                    <Eye weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                    Preview / edit Client Care Letter
                    {cclOverride && (
                      <span className="ml-1.5 text-[9px] uppercase tracking-wide bg-amber-100 text-amber-700 px-1 py-0.5 rounded font-semibold">Custom</span>
                    )}
                  </Button>
                </div>
              </label>
            </div>
          </>
        )}

        <DialogFooter className="border-t border-slate-100 pt-3 mt-2">
          <div className="flex-1 text-sm text-slate-600 self-center" data-testid="pack-summary">
            {picked.size > 0 && <><strong>{picked.size} document{picked.size === 1 ? "" : "s"}</strong> selected</>}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="pack-cancel">Cancel</Button>
          <Button onClick={handleSubmit}
            disabled={submitting || (!picked.size && !sendLink) || (sendLink && !recipients.length)}
            className="bg-[#100f5d] hover:bg-[#0b0a3d] text-white"
            data-testid="pack-generate">
            <Package weight="bold" className="w-4 h-4 mr-1" />
            {submitting ? "Working…" : primaryLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
      <CclPreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} caseId={caseId} fileHandlerId={fileHandlerId} onOverrideChange={setCclOverride} />
    </Dialog>
  );
}
